import { FC, useState } from 'react';
import axios from 'axios';
import { CertificatesModal } from 'src/Containers';
import useToggle from './useToggle';
import { ICertificateInfo } from 'src/Types/Certificate';
import API from 'src/API';
import useIsWaiting from './useIsWaiting';

type DataForSignType = {
	fileId: string;
	attachmentId: string;
	componentId: string;
};

type useSignHandler = (
	hashFromServerUrl?: string,
	saveSignatureurl?: string,
	saveCallback?: (data: any) => void,
	callbackAfterChange?: (cert: ICertificateInfo) => void,
) => [
	(data?: DataForSignType) => void,
	ICertificateInfo | null,
	FC,
	(
		fileId: string,
		attachmentId: string,
		componentId: string,
		thumbprint?: string,
	) => Promise<any>,
];

const useSign: useSignHandler = (
	hashFromServerUrl,
	saveSignatureurl,
	saveCallback,
	callbackAfterChange,
) => {
	const { showWaiting, setWaiting } = useIsWaiting();
	const [isShowSign, toggleShowSign] = useToggle(false);
	const [dataForSign, setDataForSign] = useState<DataForSignType>();
	const [certificate, setCertificate] = useState<ICertificateInfo | null>(
		null,
	);

	const askCert = (data?: DataForSignType) => {
		if (isShowSign) {
			return;
		}
		if (data) {
			setDataForSign(data);
		}
		toggleShowSign();
	};

	const onChangeCert = (cert: ICertificateInfo) => {
		setCertificate(cert);
		if (callbackAfterChange) {
			callbackAfterChange(cert);
		}
		if (dataForSign) {
			sign(
				dataForSign.fileId,
				dataForSign.attachmentId,
				dataForSign.componentId,
				cert.thumbprint,
			);
		}
	};

	const renderModal: FC = () => {
		return (
			<CertificatesModal
				isOpen={isShowSign}
				toggleOpen={toggleShowSign}
				onChangeCert={onChangeCert}
				currentThumbprint=""
			/>
		);
	};

	const sign = async (
		fileId: string,
		attachmentId: string,
		componentId: string,
		thumbprint?: string,
	) => {
		showWaiting('Подписывание...');
		const th = thumbprint ? thumbprint : certificate?.thumbprint!;
		const certificatePromise =
			await API.Certificate.getCertInfoByThumbprint(th);
		const data: any = {
			fileId,
			attachmentId,
			componentId,
			thumbprint,
			certificate: certificatePromise.base64,
		};
		const hashFromServer = await axios.post(
			window.host + hashFromServerUrl,
			data,
			{ withCredentials: true },
		);
		if (hashFromServer.status === 200 && !hashFromServer.data.hasError) {
			data.digest = hashFromServer.data.digest;
			data.hashAlgId = hashFromServer.data.hashAlgId;
			const signedHashData = await API.Certificate.signHash(
				data.digest,
				data.hashAlgId,
				th,
			);
			if (signedHashData.success) {
				const newData = {
					...data,
					digest: signedHashData.data,
				};
				return await saveSignatureAjax(newData);
			}
		}
	};

	const saveSignatureAjax = async (data: any) => {
		const result = await axios.post(window.host + saveSignatureurl, data, {
			withCredentials: true,
		});
		if (result.status === 200 && !result.data.hasError) {
			if (saveCallback) {
				saveCallback(result.data);
			}
			setWaiting(false);
			return result.data;
		}
	};

	return [askCert, certificate, renderModal, sign];
};

export default useSign;
